import { faUpload } from '@fortawesome/free-solid-svg-icons'
import { useMemo, useState } from 'react'
import {
	addStructureToRelease,
	createSystemRelease,
	getAllNodeHistory,
	getStructureDetailV1,
} from 'src/endpoints'

import { Flex } from '@/components/Layout'
import { ModalForm } from '@/components/Modal/ModalForm'
import {
	CheckBoxFormField,
	SelectFormField,
	TextAreaFormField,
} from '@/components/UberForm'
import { useTabContext } from '@/context/TabContext/TabContext'
import { getApiRef, useApiRequest } from '@/endpoints/hooks'
import { StructureDto, StructureObjectDto } from '@/endpoints/models'
import {
	useAppContext,
	useAppDispatch,
	useAppStore,
	useResolveSystemId,
} from '@/hooks'
import { RELEASE_OBJECTS_FORBIDDEN } from '@/pages/User/pages/Home/pages/SystemDetail/pages/DeploymentPackages/constants'
import { useSelectedBranchNumericId } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import { useGetSystemReleasesUsingGetQuery } from '@/rtkApi'
import { mddApiV2 } from '@/rtkApi/mddApiV2'
import { useBulkUnlockMutation } from '@/rtkApi/mddApiV2'
import { pushHistory } from '@/store/modules/history/actions'
import { loadNodeChildren, unlockNode } from '@/store/modules/node/actions'
import { loadNode } from '@/store/modules/node/general-actions'
import { cancelEditingTab } from '@/store/modules/tab/actions'
import { updateDialogSettings } from '@/store/modules/user/actions'
import { clearDeletedNodes } from '@/utils'
import { logError } from '@/utils/errorHandling'

import { useTreePanelOperations } from '../TreePanel/TreePanelContext'
import { ValidationsModal } from './components/ValidationsModal'
import DpCreationSelectOption from './DpCreationSelectOption'
import { usePrePushValidation } from './hooks/usePrePushValidation'
import {
	type FormDataPushModal,
	type PushModalProps,
	type ReleaseResponse,
} from './types'

const FIX_IMPACTS_ELEMENT_TYPES = [StructureObjectDto.TypeEnum.TABLE]

export const PushModal = ({
	node: nodeInit,
	onExitEditMode,
	onClose,
	beforePush,
	afterPush,
	nodes,
}: PushModalProps) => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const { onSaveError } = useTabContext()
	const { refreshSystemsSilent } = useTreePanelOperations()

	const [bulkUnlock] = useBulkUnlockMutation()
	const branchId = useSelectedBranchNumericId()

	const { isLoading, validations } = usePrePushValidation(nodeInit?.id)
	const [isReleaseLoading, setIsReleaseLoading] = useState(false)
	const [inputValue, setInputValue] = useState('')

	const dialog = useAppStore((state) => state.user.dialog.settings)

	const node = useMemo(
		() => (nodeInit ? nodeInit : nodes?.[0]),
		[nodeInit, nodes],
	) as StructureDto

	const { systemId, resolvingSystemId } = useResolveSystemId(node)

	const systemNode = useAppStore((state) => state.node.nodes[systemId])

	const request = useApiRequest()

	const handleCreateReleaseResponse = (response: ReleaseResponse) => {
		setIsReleaseLoading(false)
		setInputValue('')
		return response
	}

	const handleCreateReleaseFailure = (error: unknown) => {
		logError(error, { operation: 'PushModal.createRelease' })
		throw error
	}

	const createRelease = async (systemNode: StructureDto, name: string) => {
		try {
			const response = await request(
				createSystemRelease(systemNode?.id, {
					name: name,
					description: '',
				}),
			)
			return handleCreateReleaseResponse(response)
		} catch (error) {
			return handleCreateReleaseFailure(error)
		}
	}

	const { data: releases, isLoading: loading } =
		useGetSystemReleasesUsingGetQuery(
			{ onlyOpen: true, systemId },
			{
				skip: !systemId,
			},
		)

	const canAddToRelease = useMemo(
		() => !RELEASE_OBJECTS_FORBIDDEN.includes(node.type),
		[node.type],
	)

	const handleSubmit = async (form: Partial<FormDataPushModal>) => {
		try {
			await beforePush?.()

			// Batch edit
			if (nodes) {
				if (form.releaseId) {
					const releases = nodes.map((node) =>
						request(
							addStructureToRelease(systemId, form.releaseId!, [node.id]),
						),
					)
					await Promise.allSettled(releases)
				}

				const histories = nodes.map((node) =>
					dispatch(
						pushHistory(node, {
							description: form.description || '',
						}),
					),
				)
				await Promise.allSettled(histories)

				if (form.exitEditMode) {
					nodes.forEach((node) => dispatch(cancelEditingTab(node)))

					if (form.unlockNode) {
						const nodeIds = nodes
							.map((node) => node.id)
							.filter(Boolean) as number[]
						if (nodeIds.length > 0 && branchId != null) {
							await bulkUnlock({ branchId, id: nodeIds }).unwrap()
						}
					}
				}

				const children = nodes.map((node) =>
					dispatch(loadNodeChildren(node.id)),
				)
				await Promise.allSettled(children)

				const nodesLoaded = nodes.map((node) => dispatch(loadNode(node.id)))
				await Promise.allSettled(nodesLoaded)

				nodes.forEach((node) => {
					getApiRef(getAllNodeHistory(node.id)).invalidate()
					getApiRef(getStructureDetailV1(node.id)).invalidate()
				})

				dispatch(mddApiV2.util.invalidateTags(['Structure']))
				// Refresh tree in background so UI does not freeze
				refreshSystemsSilent().catch((e) =>
					logError(e, { operation: 'PushModal.refreshSystemsSilent' }),
				)
			}
			// Single element edit
			else {
				if (form.releaseId) {
					await request(
						addStructureToRelease(systemId, form.releaseId, [node.id]),
					)
				}

				await dispatch(
					pushHistory(
						node,
						{
							description: form.description || '',
						},
						form.fixImpacts,
					),
				)

				if (form.exitEditMode) {
					dispatch(cancelEditingTab(node))
					form.unlockNode && (await dispatch(unlockNode(node)))
					await onExitEditMode?.(node)
				}

				await dispatch(loadNodeChildren(node.id))
				await dispatch(loadNode(node.id))
				getApiRef(getAllNodeHistory(node.id)).invalidate()
				getApiRef(getStructureDetailV1(node.id)).invalidate()

				dispatch(mddApiV2.util.invalidateTags(['Structure']))
				// Refresh tree in background so UI does not freeze
				refreshSystemsSilent().catch((e) =>
					logError(e, { operation: 'PushModal.refreshSystemsSilent' }),
				)

				clearDeletedNodes()
			}
		} catch (e) {
			onSaveError(e)
		}

		afterPush?.(form)
		onClose()
	}

	return (
		<ModalForm<FormDataPushModal>
			open={true}
			saveTitle={t('PUSH')}
			testId="confirm"
			saveIcon={faUpload}
			cancelTitle={t('CANCEL')}
			onSubmit={handleSubmit}
			onClose={onClose}
			contentStyle={{ width: '440px' }}
			header={t('PUSH_MODAL_HEADER')}
			initialValues={{
				exitEditMode: dialog.keepEditOnExit,
				unlockNode: dialog.unlockOnExit,
			}}
			isLoading={isLoading || resolvingSystemId}
			footerBefore={<ValidationsModal validations={validations} />}
		>
			<TextAreaFormField
				title={t('PUSH_DESCRIPTION')}
				name="description"
				required={true}
				testId="push-description"
			/>
			<Flex>
				<CheckBoxFormField
					name="exitEditMode"
					className="flex-1"
					value={dialog.keepEditOnExit}
					onChange={(checked) => {
						dispatch(
							updateDialogSettings({
								keepEditOnExit: checked as boolean,
								unlockOnExit: checked ? dialog.unlockOnExit : false,
							}),
						)
					}}
					title={t('PUSH_EXIT_EDIT_MODE')}
				/>
				{dialog.keepEditOnExit && (
					<CheckBoxFormField
						name="unlockNode"
						className="flex-1"
						title={nodes ? t('PUSH_LOCK_NODES') : t('PUSH_LOCK_NODE')}
						value={dialog.unlockOnExit}
						onChange={(checked) => {
							dispatch(
								updateDialogSettings({ unlockOnExit: checked as boolean }),
							)
						}}
					/>
				)}
				{nodeInit &&
					FIX_IMPACTS_ELEMENT_TYPES.includes(nodeInit.type) &&
					validations.length > 0 && (
						<CheckBoxFormField
							name="fixImpacts"
							className="flex-1"
							title={t('FIX_IMPACTS')}
						/>
					)}
			</Flex>

			{canAddToRelease && (
				<div className="flex items-end">
					<SelectFormField
						name="releaseId"
						title={
							nodes
								? t('ADD_ELEMENTS_TO_DEPLOYMENT_PKG')
								: t('ADD_ELEMENT_TO_DEPLOYMENT_PKG')
						}
						options={releases}
						valueKey="id"
						labelKey="name"
						allowEmpty
						clearable
						loading={loading || resolvingSystemId}
						onInputChange={(e) => setInputValue(e)}
						components={{
							NoOptionsMessage: () => (
								<DpCreationSelectOption
									systemNode={systemNode as StructureDto}
									inputValue={inputValue}
									isReleaseLoading={isReleaseLoading}
									setIsReleaseLoading={setIsReleaseLoading}
									createRelease={createRelease}
								/>
							),
						}}
					/>
				</div>
			)}
		</ModalForm>
	)
}
